import { Component, computed, inject, signal } from '@angular/core';
import {
  ButtonComponent,
  ButtonDropdownComponent,
  ButtonType,
  CheckboxComponent,
  CheckboxFormFieldComponent,
  CollapsibleContentIconPosition,
  ContentHeaderComponent,
  DateBadgeComponent,
  DateInputComponent,
  DialogUtils,
  DropdownInputComponent,
  DualToggleComponent,
  FileListItemComponent,
  FileUploadFormFieldComponent,
  FileUtils,
  FilterButtonComponent,
  GeneralStatusPillComponent,
  InfoLabelComponent,
  InfoLabelSize,
  InputSize,
  InteractivePillComponent,
  LargeBannerComponent,
  LocalizedTextPipe,
  PasswordFormFieldComponent,
  PhoneInputComponent,
  PostalCodeFormFieldComponent,
  PostalCodeInputComponent,
  ProgressStepperComponent,
  RadioButtonComponent,
  RateInputComponent,
  ReadonlyFormFieldComponent,
  RichTextFormFieldComponent,
  SelectAllCheckboxFormFieldComponent,
  SmallBannerComponent,
  StatusPillType,
  TextFormFieldComponent,
  TextInputComponent,
  ToggleComponent,
  TypeaheadInputComponent,
} from '../shared/';
import {
  BannerStyle,
  CollapsibleContentComponent,
  CurrencyInputComponent,
  DropdownItem,
  DualToggleState,
  ImageCropperDialogComponent,
  LoadingInputComponent,
  postalCodeValidator,
  RepeatDirective,
  signalCountryChangeRegistrar,
  TextareaInputComponent,
  TooltipDirective,
} from '../shared';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CountryType, LocalizedTextService } from '../core';
import { DndDirective } from '../shared/directives/dnd-directive';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-gallery',
  imports: [
    DualToggleComponent,
    DateInputComponent,
    DropdownInputComponent,
    ButtonDropdownComponent,
    ButtonComponent,
    ContentHeaderComponent,
    FilterButtonComponent,
    PhoneInputComponent,
    RadioButtonComponent,
    ToggleComponent,
    TextInputComponent,
    TypeaheadInputComponent,
    CheckboxComponent,
    CollapsibleContentComponent,
    FileListItemComponent,
    FileUploadFormFieldComponent,
    ReactiveFormsModule,
    RichTextFormFieldComponent,
    PasswordFormFieldComponent,
    ReadonlyFormFieldComponent,
    SmallBannerComponent,
    LargeBannerComponent,
    ProgressStepperComponent,
    SelectAllCheckboxFormFieldComponent,
    CheckboxFormFieldComponent,
    CurrencyInputComponent,
    TextareaInputComponent,
    RateInputComponent,
    TextFormFieldComponent,
    InteractivePillComponent,
    GeneralStatusPillComponent,
    LoadingInputComponent,
    DateBadgeComponent,
    InfoLabelComponent,
    RepeatDirective,
    TooltipDirective,
    DndDirective,
    LocalizedTextPipe,
    PostalCodeInputComponent,
    PostalCodeFormFieldComponent,
  ],
  templateUrl: './gallery.component.html',
  styleUrl: './gallery.component.scss',
  standalone: true,
})
export class GalleryComponent {
  dialog = inject(MatDialog);
  readonly localizedTextService = new LocalizedTextService('fr-Ca');

  protected readonly $localize = $localize;
  protected readonly FileUtils = FileUtils;
  dualToggleTextState = signal<DualToggleState>(DualToggleState.left);
  dualToggleCompactTextState = signal<DualToggleState>(DualToggleState.left);
  dualToggleIconsState = signal<DualToggleState>(DualToggleState.right);

  toggleState = signal<boolean>(false);

  radioState1 = signal<boolean>(true);
  radioState2 = signal<boolean>(false);

  checkboxState = signal<boolean>(false);
  repeatDirectiveCount = signal<number>(3);
  directiveDroppedFileNames = signal<string[]>([]);

  dropdownItems = signal<DropdownItem[]>([
    { title: 'Option 1', value: '1', disabled: false },
    { title: 'Option 2', value: '2', disabled: false },
    { title: 'Option 3', value: '3', disabled: false },
  ]);

  progressStepperSteps = ['Step 1', 'Step 2', 'Step 3', 'Step 4'];
  currentStep = signal<number>(1);

  filterBadgeCount = signal<number>(3);
  phoneFormControl = new FormControl('CA');

  fileFormControl = new FormControl<File[]>([], { nonNullable: true });
  files = signal<File[]>([]);
  fileControlListener = this.fileFormControl.valueChanges.subscribe(newFiles => {
    if (newFiles) {
      this.files.set(newFiles);
    }
  });

  // Date badge
  startDate = new Date('2026-02-01T00:00');
  endDate = new Date('2026-02-04T00:00');
  endDateSpanMonth = new Date('2026-03-04T00:00');
  endDateSpanYear = new Date('2027-02-04T00:00');
  endDateSpanMonthYear = new Date('2027-03-04T00:00');

  disabledTextFormControl = new FormControl({ value: 'Disabled', disabled: true }, { nonNullable: true });
  postalCodeCountryFormControl = new FormControl<CountryType>(CountryType.Canada, { nonNullable: true });
  postalCodeCountry = toSignal(this.postalCodeCountryFormControl.valueChanges, { initialValue: CountryType.Canada });
  postalCodeFormControl = new FormControl('', [
    Validators.required,
    postalCodeValidator(() => this.postalCodeCountry(), signalCountryChangeRegistrar(this.postalCodeCountry)),
  ]);
  postalCodeCountryDropdownItems = computed(() => [
    new DropdownItem<CountryType>('Canada', CountryType.Canada),
    new DropdownItem<CountryType>('USA', CountryType.US),
  ]);
  richTextFormControl = new FormControl('');
  passwordFormControl = new FormControl('', [Validators.required]);
  child1CheckboxFormControl = new FormControl<boolean>(false, { nonNullable: true });
  child2CheckboxFormControl = new FormControl<boolean>(false, { nonNullable: true });
  child3CheckboxFormControl = new FormControl<boolean>(false, { nonNullable: true });
  child4CheckboxFormControl = new FormControl<boolean>(false, { nonNullable: true });
  childCheckboxFormControls = [
    this.child1CheckboxFormControl,
    this.child2CheckboxFormControl,
    this.child3CheckboxFormControl,
    this.child4CheckboxFormControl,
  ];

  protected readonly DualToggleState = DualToggleState;
  protected readonly ButtonType = ButtonType;
  protected readonly InputSize = InputSize;
  protected readonly BannerStyle = BannerStyle;
  protected readonly StatusPillType = StatusPillType;
  protected readonly InfoLabelSize = InfoLabelSize;
  protected readonly CountryType = CountryType;

  protected readonly CollapsibleContentIconPosition = CollapsibleContentIconPosition;
  increaseStep() {
    this.currentStep.update(stepNum => (stepNum >= 4 ? stepNum : stepNum + 1));
  }

  decreaseStep() {
    this.currentStep.update(stepNum => (stepNum <= 1 ? stepNum : stepNum - 1));
  }

  onDualToggleTextChange(state: DualToggleState) {
    this.dualToggleTextState.set(state);
    console.warn('Dual Toggle Text changed to:', state);
  }

  onDualToggleCompactTextChange(state: DualToggleState) {
    this.dualToggleCompactTextState.set(state);
    console.warn('Dual Toggle Compact Text changed to:', state);
  }

  onDualToggleIconsChange(state: DualToggleState) {
    this.dualToggleIconsState.set(state);
    console.warn('Dual Toggle 2 changed to:', state);
  }

  onToggleChange() {
    this.toggleState.update(state => !state);
    console.warn('Toggle changed to:', this.toggleState());
  }

  onButtonClick(input: string | undefined | null) {
    console.warn(`${input} button clicked'`);
  }

  onRadio1Click() {
    this.radioState1.set(true);
    this.radioState2.set(false);
    console.warn('Radio 1 selected');
  }

  onRadio2Click() {
    this.radioState1.set(false);
    this.radioState2.set(true);
    console.warn('Radio 2 selected');
  }

  onCheckboxChange() {
    this.checkboxState.update(state => !state);
    console.warn('Checkbox 1 changed to:', this.checkboxState());
  }

  onDropdownItemSelected() {
    console.warn('Dropdown item selected');
  }

  onFilterClick() {
    console.warn('Filter button clicked');
  }

  onTypeaheadValueChange(value: string | null) {
    console.warn('Typeahead value changed:', value);
  }

  increaseDirectiveRepeatCount() {
    this.repeatDirectiveCount.update(count => count + 1);
  }

  decreaseDirectiveRepeatCount() {
    this.repeatDirectiveCount.update(count => (count <= 1 ? 1 : count - 1));
  }

  onDirectiveFileDropped(fileList: FileList) {
    this.directiveDroppedFileNames.set(Array.from(fileList).map(file => file.name));
  }

  clearDirectiveDroppedFiles() {
    this.directiveDroppedFileNames.set([]);
  }

  toggleRichTextEnabled() {
    if (this.richTextFormControl.disabled) {
      this.richTextFormControl.enable();
    } else {
      this.richTextFormControl.disable();
    }
  }

  trailingIconClick() {
    console.warn('Trailing icon clicked on readonly-form-field');
  }

  bannerButtonClicked() {
    console.warn('Banner button clicked');
  }

  bannerCloseButtonClicked() {
    console.warn(`Banner close button clicked`);
  }

  popCropModal() {
    const dialogRef = this.dialog.open(ImageCropperDialogComponent, DialogUtils.smallDialogConfig());
    dialogRef.componentInstance.imageFiles.set(this.files());

    dialogRef.afterClosed().subscribe((result: File[] | null) => {
      if (result) {
        this.files.set(result);
      }
    });
  }
}
